import { useT } from '@transifex/react'
import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react'
import { getMentionPreviewRegionId } from './MentionsPopper'

const MENTION_A11Y_ATTR = 'data-mention-a11y'

type UseHtmlContentWithMentionsProps = {
  htmlContent: string
  onNavigate: (mentionId: string) => void
}

type UseHtmlContentWithMentionsReturn = {
  containerRef: React.RefObject<HTMLDivElement>
  anchorEl: HTMLElement | null
  tenantMemberId: string | null
  previewLoading: boolean
  setPreviewLoading: (loading: boolean) => void
}

export const useHtmlContentWithMentions = ({
  htmlContent,
  onNavigate,
}: UseHtmlContentWithMentionsProps): UseHtmlContentWithMentionsReturn => {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null)
  const [tenantMemberId, setTenantMemberId] = useState<string | null>(null)
  const [previewLoading, setPreviewLoading] = useState(false)
  const containerRef = useRef<HTMLDivElement>(null)
  const lastPreviewAnchorRef = useRef<HTMLElement | null>(null)
  const tenantMemberIdRef = useRef<string | null>(null)
  const t = useT()

  tenantMemberIdRef.current = tenantMemberId

  const getMentionFromEvent = useCallback((event: Event): HTMLElement | null => {
    const container = containerRef.current
    if (!container) {
      return null
    }
    const target = event.target as HTMLElement
    const mention = target.closest<HTMLElement>('[data-type="mention"]')
    if (!mention || !container.contains(mention)) {
      return null
    }
    return mention
  }, [])

  const clearPreviewAria = useCallback((el: HTMLElement) => {
    el.removeAttribute('aria-describedby')
    el.removeAttribute('aria-expanded')
    el.removeAttribute('aria-busy')
  }, [])

  const openPreview = useCallback((mention: HTMLElement) => {
    const mentionId = mention.getAttribute('data-id')
    if (mentionId) {
      setAnchorEl(mention)
      setTenantMemberId(mentionId)
    }
  }, [])

  const closePreview = useCallback(() => {
    setAnchorEl(null)
    setTenantMemberId(null)
  }, [])

  const handleMouseOver = useCallback(
    (event: Event) => {
      const mention = getMentionFromEvent(event)
      if (!mention) {
        return
      }
      event.stopPropagation()
      openPreview(mention)
    },
    [getMentionFromEvent, openPreview],
  )

  const handleMouseOut = useCallback(
    (event: Event) => {
      const mention = getMentionFromEvent(event)
      if (!mention) {
        return
      }
      const related = (event as MouseEvent).relatedTarget as Node | null
      if (related && mention.contains(related)) {
        return
      }
      const active = document.activeElement
      if (active !== null && mention.contains(active)) {
        return
      }
      event.stopPropagation()
      closePreview()
    },
    [getMentionFromEvent, closePreview],
  )

  const handleMouseClick = useCallback(
    (event: Event) => {
      const mention = getMentionFromEvent(event)
      if (!mention) {
        return
      }
      event.stopPropagation()
      const mentionId = mention.getAttribute('data-id')
      if (mentionId) {
        onNavigate(mentionId)
      }
    },
    [getMentionFromEvent, onNavigate],
  )

  const handleFocusIn = useCallback(
    (event: Event) => {
      const mention = getMentionFromEvent(event)
      if (!mention) {
        return
      }
      event.stopPropagation()
      openPreview(mention)
    },
    [getMentionFromEvent, openPreview],
  )

  const handleFocusOut = useCallback(
    (event: Event) => {
      const mention = getMentionFromEvent(event)
      if (!mention) {
        return
      }
      const related = (event as FocusEvent).relatedTarget as Node | null
      if (related && mention.contains(related)) {
        return
      }
      event.stopPropagation()
      closePreview()
    },
    [getMentionFromEvent, closePreview],
  )

  const handleKeyDown = useCallback(
    (event: KeyboardEvent) => {
      if (event.key !== 'Enter' && event.key !== ' ') {
        return
      }
      const mention = getMentionFromEvent(event)
      if (!mention) {
        return
      }
      const mentionId = mention.getAttribute('data-id')
      if (!mentionId) {
        return
      }
      event.preventDefault()
      event.stopPropagation()
      onNavigate(mentionId)
    },
    [getMentionFromEvent, onNavigate],
  )

  useLayoutEffect(() => {
    const container = containerRef.current
    if (!container) {
      return
    }
    const mentions = container.querySelectorAll<HTMLElement>('span[data-type="mention"][data-id]')
    mentions.forEach(mention => {
      if (mention.getAttribute(MENTION_A11Y_ATTR)) {
        return
      }
      mention.setAttribute(MENTION_A11Y_ATTR, 'true')
      mention.setAttribute('tabindex', '0')
      mention.setAttribute('role', 'button')
      const labelName = mention.textContent?.trim() ?? ''
      mention.setAttribute('aria-label', t('View profile for {name}', { name: labelName }))
    })

    return () => {
      container
        .querySelectorAll<HTMLElement>(`span[data-type="mention"][${MENTION_A11Y_ATTR}]`)
        .forEach(mention => {
          mention.removeAttribute('tabindex')
          mention.removeAttribute('role')
          mention.removeAttribute('aria-label')
          mention.removeAttribute(MENTION_A11Y_ATTR)
          mention.removeAttribute('aria-describedby')
          mention.removeAttribute('aria-expanded')
          mention.removeAttribute('aria-busy')
        })
    }
  }, [htmlContent, t])

  useLayoutEffect(() => {
    const prev = lastPreviewAnchorRef.current
    if (prev && prev !== anchorEl) {
      clearPreviewAria(prev)
    }
    if (anchorEl && tenantMemberId) {
      anchorEl.setAttribute('aria-describedby', getMentionPreviewRegionId(tenantMemberId))
      anchorEl.setAttribute('aria-expanded', 'true')
      if (previewLoading) {
        anchorEl.setAttribute('aria-busy', 'true')
      } else {
        anchorEl.removeAttribute('aria-busy')
      }
      lastPreviewAnchorRef.current = anchorEl
    } else {
      if (lastPreviewAnchorRef.current) {
        clearPreviewAria(lastPreviewAnchorRef.current)
      }
      lastPreviewAnchorRef.current = null
    }
  }, [anchorEl, tenantMemberId, previewLoading, clearPreviewAria])

  useLayoutEffect(() => {
    const onDocumentKeyDown = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') {
        return
      }
      if (!tenantMemberIdRef.current) {
        return
      }
      event.preventDefault()
      event.stopPropagation()
      closePreview()
    }
    document.addEventListener('keydown', onDocumentKeyDown)
    return () => document.removeEventListener('keydown', onDocumentKeyDown)
  }, [closePreview])

  useEffect(() => {
    const container = containerRef.current
    if (!container) {
      return
    }
    container.addEventListener('mouseover', handleMouseOver)
    container.addEventListener('mouseout', handleMouseOut)
    container.addEventListener('click', handleMouseClick)
    container.addEventListener('focusin', handleFocusIn)
    container.addEventListener('focusout', handleFocusOut)
    container.addEventListener('keydown', handleKeyDown)
    return () => {
      container.removeEventListener('mouseover', handleMouseOver)
      container.removeEventListener('mouseout', handleMouseOut)
      container.removeEventListener('click', handleMouseClick)
      container.removeEventListener('focusin', handleFocusIn)
      container.removeEventListener('focusout', handleFocusOut)
      container.removeEventListener('keydown', handleKeyDown)
    }
  }, [
    handleMouseOver,
    handleMouseOut,
    handleMouseClick,
    handleFocusIn,
    handleFocusOut,
    handleKeyDown,
  ])

  return {
    containerRef,
    anchorEl,
    tenantMemberId,
    previewLoading,
    setPreviewLoading,
  }
}
